import React, { createContext, useState, useContext, useEffect } from 'react';
import notify, { notifyIcons } from '../utils/notify';
import * as geolocationService from '../services/geolocation';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Request user location
  const requestUserLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      const location = await geolocationService.requestLocation();

      // Validate coordinates
      if (!geolocationService.isValidCoordinates(location.latitude, location.longitude)) {
        throw new Error('Invalid coordinates received');
      }

      setUserLocation(location);
      setLocationPermission('granted');

      // Save to localStorage
      localStorage.setItem('user_location', JSON.stringify({
        ...location,
        timestamp: Date.now(),
      }));

      // Обычное подтверждение — заливка не нужна (брендбук 8.2)
      notify('Геолокация получена', { icon: notifyIcons.location });

      return location;
    } catch (err) {
      setError(err.message);

      const { LOCATION_ERROR } = geolocationService;

      // 'denied' показывает экран с инструкцией по выдаче доступа —
      // ставим его только при реальном отказе, а не при любой ошибке
      switch (err.code) {
        case LOCATION_ERROR.DENIED:
          setLocationPermission('denied');
          notify.error('Разрешите геолокацию в настройках');
          break;
        case LOCATION_ERROR.TIMEOUT:
          notify.error('Геолокация не отвечает, попробуйте ещё раз');
          break;
        case LOCATION_ERROR.UNSUPPORTED:
          notify.error('Устройство не поддерживает геолокацию');
          break;
        default:
          notify.error('Не удалось определить местоположение');
      }

      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear saved location
  const clearLocation = () => {
    setUserLocation(null);
    setLocationPermission(null);
    setError(null);
    localStorage.removeItem('user_location');
  };

  // Check if has location
  const hasLocation = () => {
    return userLocation !== null;
  };

  // Get distance to place
  const getDistanceToPlace = (place) => {
    if (!userLocation || !place) {
      return null;
    }

    const placeLat = parseFloat(place.latitude);
    const placeLon = parseFloat(place.longitude);

    if (!geolocationService.isValidCoordinates(placeLat, placeLon)) {
      return null;
    }

    const distance = geolocationService.calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      placeLat,
      placeLon
    );

    return geolocationService.formatDistance(distance);
  };

  // Get raw distance to place (in meters)
  const getRawDistanceToPlace = (place) => {
    if (!userLocation || !place) {
      return Infinity;
    }

    const placeLat = parseFloat(place.latitude);
    const placeLon = parseFloat(place.longitude);

    if (!geolocationService.isValidCoordinates(placeLat, placeLon)) {
      return Infinity;
    }

    return geolocationService.calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      placeLat,
      placeLon
    );
  };

  // Load location from localStorage on mount
  useEffect(() => {
    const savedLocation = localStorage.getItem('user_location');

    if (savedLocation) {
      try {
        const { latitude, longitude, timestamp } = JSON.parse(savedLocation);

        // Check if location is not too old (1 hour)
        const age = Date.now() - timestamp;
        const maxAge = 60 * 60 * 1000; // 1 hour

        if (age < maxAge && geolocationService.isValidCoordinates(latitude, longitude)) {
          setUserLocation({ latitude, longitude });
          setLocationPermission('granted');
        } else {
          // Location is too old, clear it
          localStorage.removeItem('user_location');
        }
      } catch (e) {
        localStorage.removeItem('user_location');
      }
    }
  }, []);

  const value = {
    userLocation,
    locationPermission,
    loading,
    error,
    requestUserLocation,
    clearLocation,
    hasLocation,
    getDistanceToPlace,
    getRawDistanceToPlace,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
